import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { QueryErrorScreen } from '@/shared/components/ui/query-error-screen'
import { CategoryFilter } from '@/shared/components/category-filter/CategoryFilter'
import { BusinessServicesTabSkeleton } from '../ServicesSkeletons'
import {
  PACKAGE_TYPE_LABELS,
  PACKAGE_TYPE_ORDER,
  normalizePackageType,
} from '../../constants/serviceCatalogMeta'
import { formatServicePrice } from '../../utils/formatServicePrice'
import { usePackageCatalog } from '../../hooks/usePackageCatalog'
import { useBusinessPackages, type BusinessPackage } from '../../hooks/useBusinessPackages'
import { useRemovePackageFromBusiness } from '../../hooks/useRemovePackageFromBusiness'
import { AddServiceDialog } from './AddServiceDialog'

const CHANGE_NOTICE = 'El cambio se aplicará en el próximo ciclo de facturación.'

interface BusinessServicesTabProps {
  businessId: string
}

export function BusinessServicesTab({ businessId }: BusinessServicesTabProps) {
  const { packages, loading, error, refetch } = useBusinessPackages(businessId)
  const { removePackage, loading: removing } = useRemovePackageFromBusiness(businessId)
  const { currencyNameById } = usePackageCatalog()

  const [addOpen, setAddOpen] = useState(false)
  const [toRemove, setToRemove] = useState<BusinessPackage | null>(null)
  const [typeLabel, setTypeLabel] = useState<string | null>(null)

  const contractedServiceIds = packages.map((p) => p.packageServiceId)
  const labelOf = (p: BusinessPackage) => PACKAGE_TYPE_LABELS[normalizePackageType(p.type)]
  // Tipos presentes (Planes/Módulos/Servicios), en el orden fijo del catálogo.
  const typeLabels = PACKAGE_TYPE_ORDER.map((t) => PACKAGE_TYPE_LABELS[t]).filter((label) =>
    packages.some((p) => labelOf(p) === label),
  )
  const visiblePackages = typeLabel
    ? packages.filter((p) => labelOf(p) === typeLabel)
    : packages

  const handleRemove = async () => {
    if (!toRemove) return
    const ok = await removePackage(toRemove.id)
    if (ok) {
      toast.success('Servicio eliminado', { description: CHANGE_NOTICE })
      setToRemove(null)
    }
  }

  if (error && packages.length === 0) {
    return <QueryErrorScreen error={error} onRetry={() => refetch()} />
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ink-muted">
          Servicios contratados por este negocio.
        </p>
        <Button type="button" variant="primary" size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" />
          Agregar servicio
        </Button>
      </div>

      {loading ? (
        <BusinessServicesTabSkeleton />
      ) : packages.length === 0 ? (
        <p className="rounded-card border border-dashed border-line p-6 text-center text-sm text-ink-muted">
          Este negocio no tiene servicios contratados todavía.
        </p>
      ) : (
        <>
          {typeLabels.length > 0 && (
            <CategoryFilter
              categories={typeLabels}
              value={typeLabel}
              onChange={setTypeLabel}
            />
          )}

          {visiblePackages.length === 0 ? (
            <p className="rounded-card border border-dashed border-line p-6 text-center text-sm text-ink-muted">
              No hay servicios de este tipo.
            </p>
          ) : (
            <ul className="divide-y divide-line rounded-card border border-line">
              {visiblePackages.map((pkg) => (
                <li
                  key={pkg.id}
                  className="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">
                      {pkg.packageServiceDetails?.name ??
                        pkg.packageServiceDetails?.code ??
                        `Servicio #${pkg.packageServiceId}`}
                    </p>
                    {pkg.packageServiceDetails?.description && (
                      <p className="truncate text-xs text-ink-muted">
                        {pkg.packageServiceDetails.description}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <span className="text-sm font-semibold text-ink">
                      {formatServicePrice(
                        pkg.amount ?? 0,
                        pkg.currencyId != null
                          ? currencyNameById.get(pkg.currencyId)
                          : undefined,
                      )}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setToRemove(pkg)}
                    >
                      <Trash2 className="h-4 w-4" />
                      Quitar
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <AddServiceDialog
        businessId={businessId}
        open={addOpen}
        onOpenChange={setAddOpen}
        contractedServiceIds={contractedServiceIds}
        onAdded={() => toast.success('Servicio agregado', { description: CHANGE_NOTICE })}
      />

      <Dialog open={!!toRemove} onOpenChange={(open) => !open && setToRemove(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Quitar servicio</DialogTitle>
            <DialogDescription>
              {toRemove ? (
                <>
                  Vas a quitar{' '}
                  <strong>
                    {toRemove.packageServiceDetails?.name ??
                      `Servicio #${toRemove.packageServiceId}`}
                  </strong>{' '}
                  de este negocio. {CHANGE_NOTICE}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setToRemove(null)}
              disabled={removing}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleRemove}
              disabled={removing}
            >
              {removing ? 'Quitando…' : 'Quitar servicio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
